import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { Button, Tooltip, CircularProgress } from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import StockReportService from 'services/StockReportService';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import AlertHelper from 'helpers/AlertHelper';
import Error from 'components/utils/Alert/Error';
import { AlertMessagesContext } from 'react-alert-messages';
import CsvHelper from 'helpers/CsvHelper';
import PrintIcon from '@mui/icons-material/Print';
import Grayout from 'components/utils/GrayOut/Grayout';
import { useTranslation } from 'react-i18next';

function ItemStockExpiredReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [filteredReportData, setFilteredReportData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [errorMsg, setErrorMsg] = useState();

  const CSV_COLUMNS = {
    plu: tCommon('PLU'),
    name: tCommon('Name'),
    unit: tCommon('Unit'),
    category: tCommon('Category'),
    batch_id: tCommon('Batch ID'),
    batch_name: tCommon('Batch Name'),
    stock: tCommon('Stock'),
    expiry_date: tCommon('Expiry Date'),
    status: {
      title: tCommon('Status'),
      getValue: (row) =>
        DateTimeHelpers.getExpiryStatus(row.expiry_date, tCommon('expired'), tCommon('remaining')).text,
    },
  };

  const loadReport = useCallback(async () => {
    setLoadingIndicator(true);
    try {
      const resp = await StockReportService.getExpiredStockReport();
      const data = resp?.data || resp || [];
      setReportData(data);
      setFilteredReportData(data);
    } catch (err) {
      AlertHelper.setShortMessage(setErrorMsg, err.message);
    } finally {
      setLoadingIndicator(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleSearch = (value) => {
    setSearchText(value);
    const lowerValue = value.toLowerCase();
    setFilteredReportData(
      reportData.filter(
        (item) =>
          item.name?.toLowerCase().includes(lowerValue) ||
          item.plu?.toString().includes(lowerValue) ||
          item.batch_name?.toLowerCase().includes(lowerValue) ||
          item.category?.toLowerCase().includes(lowerValue)
      )
    );
  };

  const toCsvBtnPressed = () => {
    const dateTime = DateTimeHelpers.getDate(new Date());
    const csv = CsvHelper.getString(filteredReportData, CSV_COLUMNS, 'Item Stock Expired Report');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `ITEM-STOCK-EXPIRED-REPORT-${dateTime}.csv`);
    postAlertMessage({ text: tCommon('Exported to excel successfully'), type: 'success' });
  };

  const headerData = [
    { label: tCommon('PLU'), id: 'plu', type: 'text' },
    { label: tCommon('Name'), id: 'name', type: 'text' },
    { label: tCommon('Unit'), id: 'unit', type: 'text' },
    { label: tCommon('Category'), id: 'category', type: 'text' },
    { label: tCommon('Batch Name'), id: 'batch_name', type: 'text' },
    { label: tCommon('Stock'), id: 'stock', type: 'floatQuantity' },
    {
      label: tCommon('Expiry Date'),
      id: 'expiry_date',
      type: 'callback',
      viewRender: (row) => {
        if (!row.expiry_date) return '-';
        return DateTimeHelpers.formatDateTime(row.expiry_date);
      },
    },
  ];

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={window.print}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            {tCommon('CSV')}
          </Button>
        </div>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>{tReport('Item Stock Expired Report')}</h2>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                className={styles.searchInput}
                placeholder={tReport('Search items')}
              />
              <Tooltip title={tCommon('Search')} placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Refresh Report')}</label>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a' }}
              onClick={loadReport}
              disabled={loadingIndicator}
            >
              {loadingIndicator ? (
                <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
              ) : (
                <SyncIcon className={styles.actionBtnIcon} />
              )}
              {tCommon('Refresh')}
            </Button>
          </div>
        </div>
      </div>
      {errorMsg && (
        <div className={styles.marginTop}>
          <Error title={errorMsg} />
        </div>
      )}
      {(loadingIndicator || filteredReportData.length > 0) && (
        <div className={styles.tableContainer} style={{ marginTop: '20px' }}>
          <Grayout open={loadingIndicator} />
          <DataTable columns={headerData} rows={filteredReportData} isLoading={loadingIndicator} />
        </div>
      )}
      {!loadingIndicator && filteredReportData.length === 0 && (
        <Info
          title={tReport('No expired stock items found')}
          content={tReport('There are no items matching the expired stock criteria')}
        />
      )}
    </div>
  );
}

export default withConsoleBase(ItemStockExpiredReport);
